import React from 'react';

/**
 * Keyboard/ARIA behavior for a lightweight popover menu:
 * - Escape closes the menu and restores focus to the trigger button
 * - ArrowUp/ArrowDown cycle focus through [role="menuitem"] elements
 * - Focus moves to the first item when the menu opens
 * - Focus returns to the trigger whenever the menu closes
 */
export function useMenu(open: boolean, onClose: () => void) {
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const prevOpen = React.useRef(false);

  const getItems = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
    );

  React.useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        triggerRef.current?.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = getItems();
      if (items.length === 0) return;
      e.preventDefault();
      const idx = items.indexOf(document.activeElement as HTMLElement);
      const next =
        e.key === 'ArrowDown'
          ? items[(idx + 1 + items.length) % items.length]
          : items[(idx - 1 + items.length) % items.length];
      next?.focus();
    }

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus the first item when the menu opens (standard menu behavior).
  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      getItems()[0]?.focus();
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Restore focus to the trigger whenever the menu closes (Escape, click-outside, item click).
  React.useEffect(() => {
    if (prevOpen.current && !open) triggerRef.current?.focus();
    prevOpen.current = open;
  }, [open]);

  return { menuRef, triggerRef };
}
